import React, { useState, useEffect, useCallback } from 'react';

/**
 * Caregivers tab — shows who can access this managed account, lets a manager
 * invite a co-caregiver by email, remove a co-caregiver, and see pending
 * invites. Uses /api/managed-accounts/{id}/caregivers + .../invite + DELETE.
 */
export default function CareCaregiversTab({ account, token, api }) {
  const [data, setData] = useState(null);       // {caregivers, pending_invites}
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [relationship, setRelationship] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      const r = await fetch(`${api}/api/managed-accounts/${encodeURIComponent(account.id)}/caregivers`,
        { headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
      setData(d);
    } catch (e) { setError(e.message); setData({ caregivers: [], pending_invites: [] }); }
  }, [api, token, account.id]);

  useEffect(() => { load(); }, [load]);

  const invite = async (e) => {
    e?.preventDefault?.();
    if (!email.trim()) return;
    setBusy(true); setError(''); setNotice('');
    try {
      const r = await fetch(`${api}/api/managed-accounts/${encodeURIComponent(account.id)}/caregivers/invite`,
        { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ email: email.trim(), relationship: relationship.trim() || null }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
      setNotice(d.status === 'added' ? `${email.trim()} now has access.` : `Invite sent to ${email.trim()}.`);
      setEmail(''); setRelationship('');
      await load();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  const remove = async (managerUserId, name) => {
    if (!window.confirm(`Remove ${name || 'this caregiver'}'s access?`)) return;
    try {
      const r = await fetch(`${api}/api/managed-accounts/${encodeURIComponent(account.id)}/caregivers/${encodeURIComponent(managerUserId)}`,
        { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      await load();
    } catch (e) { setError(e.message); }
  };

  return (
    <div>
      <form onSubmit={invite} style={{ marginBottom: 18 }}>
        <div className="care-field-label">Invite a co-caregiver</div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
          <input className="care-input" type="email" placeholder="their email" value={email} onChange={(e) => setEmail(e.target.value)} style={{ flex: 2 }} />
          <input className="care-input" placeholder="relationship (e.g. son)" value={relationship} onChange={(e) => setRelationship(e.target.value)} style={{ flex: 1 }} />
        </div>
        <button type="submit" className="care-btn primary" disabled={busy || !email.trim()}>{busy ? 'Sending…' : '+ Invite'}</button>
      </form>
      {notice && <div style={{ color: '#7fa876', fontSize: 12, marginBottom: 10 }}>{notice}</div>}
      {error && <div className="care-error-state" role="alert">{error}</div>}

      {data === null ? (
        <><div className="care-skeleton" /><div className="care-skeleton" /></>
      ) : (
        <>
          {(data.caregivers || []).map((c) => (
            <div key={c.manager_user_id} className="care-list-row">
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="care-row-title">
                  {c.name || c.email}
                  {c.role === 'primary' && <span style={{ marginLeft: 8, fontSize: 11, color: '#e8c77b' }}>primary</span>}
                </div>
                <div className="care-row-meta">{c.email}{c.relationship ? ` · ${c.relationship}` : ''}</div>
              </div>
              {c.role !== 'primary' && (
                <div className="care-row-actions">
                  <button className="care-mini-btn danger" onClick={() => remove(c.manager_user_id, c.name)} aria-label="Remove caregiver">Remove</button>
                </div>
              )}
            </div>
          ))}
          {(data.pending_invites || []).map((p) => (
            <div key={p.id} className="care-list-row" style={{ opacity: 0.7 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="care-row-title">{p.invited_email}</div>
                <div className="care-row-meta">invited{p.relationship ? ` · ${p.relationship}` : ''} · pending</div>
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}
